import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, Pencil, Trash2, Package, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MedicationPresentationsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPresentation, setEditingPresentation] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    unidad_asociada: '',
    orden: 0
  });

  const canCreate = hasPermission('medicamentos', 'crear') || isSuperAdmin;
  const canEdit = hasPermission('medicamentos', 'editar') || isSuperAdmin;
  const canDelete = hasPermission('medicamentos', 'eliminar') || isSuperAdmin;

  const fetchPresentations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clinical-maintainers/medication-presentations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_inactive: showInactive }
      });
      setPresentations(response.data);
    } catch (error) {
      console.error('Error al cargar presentaciones:', error);
      toast.error('No se pudieron cargar las presentaciones de medicamentos');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchPresentations();
  }, [fetchPresentations]);

  const handleOpenModal = (presentation = null) => {
    if (presentation) {
      setEditingPresentation(presentation);
      setFormData({
        nombre: presentation.nombre || '',
        descripcion: presentation.descripcion || '',
        unidad_asociada: presentation.unidad_asociada || '',
        orden: presentation.orden || 0
      });
    } else {
      setEditingPresentation(null);
      setFormData({
        nombre: '',
        descripcion: '',
        unidad_asociada: '',
        orden: presentations.length + 1
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPresentation(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingPresentation) {
        await axios.put(`${API_URL}/api/clinical-maintainers/medication-presentations/${editingPresentation.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Presentación actualizada');
      } else {
        await axios.post(`${API_URL}/api/clinical-maintainers/medication-presentations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Presentación creada');
      }
      handleCloseModal();
      fetchPresentations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleActive = async (presentation) => {
    if (presentation.is_default) {
      toast.error('No se pueden desactivar presentaciones del sistema');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/clinical-maintainers/medication-presentations/${presentation.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Presentación ${presentation.is_active ? 'desactivada' : 'activada'}`);
      fetchPresentations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (presentation) => {
    if (presentation.is_default) {
      toast.error('No se pueden eliminar presentaciones del sistema');
      return;
    }
    if (!window.confirm(`¿Eliminar presentación "${presentation.nombre}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/clinical-maintainers/medication-presentations/${presentation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Presentación eliminada');
      fetchPresentations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="medication-presentations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-7 w-7 text-purple-600" />
            Presentaciones de Medicamentos
          </h1>
          <p className="text-gray-500">Gestión de formas de presentación de medicamentos</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              id="show-inactive"
            />
            <Label htmlFor="show-inactive" className="text-sm">Mostrar inactivas</Label>
          </div>
          <Button variant="outline" data-testid="bulk-import-link-presentations"
            onClick={() => window.location.assign('/app/admin/bulk-import?module=presentations')}>
            Carga Masiva
          </Button>
          {canCreate && (
            <Button onClick={() => handleOpenModal()} data-testid="add-presentation-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Presentación
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="w-[120px]">Unidad Asociada</TableHead>
                <TableHead className="w-[80px]">Orden</TableHead>
                <TableHead className="w-[100px]">Estado</TableHead>
                <TableHead className="w-[100px]">Tipo</TableHead>
                <TableHead className="w-[150px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : presentations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No hay presentaciones
                  </TableCell>
                </TableRow>
              ) : (
                presentations.map((presentation) => (
                  <TableRow key={presentation.id} className={!presentation.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-medium">{presentation.nombre}</TableCell>
                    <TableCell className="text-gray-600 text-sm">{presentation.descripcion || '-'}</TableCell>
                    <TableCell>
                      {presentation.unidad_asociada ? (
                        <Badge variant="outline">{presentation.unidad_asociada}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-center">{presentation.orden}</TableCell>
                    <TableCell>
                      {presentation.is_active ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Activa
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactiva
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {presentation.is_default ? (
                        <Badge variant="outline">Sistema</Badge>
                      ) : (
                        <Badge variant="secondary">Personalizada</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canEdit && !presentation.is_default && (
                          <Button variant="ghost" size="sm" onClick={() => handleOpenModal(presentation)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleToggleActive(presentation)}
                            disabled={presentation.is_default}
                          >
                            {presentation.is_active ? (
                              <XCircle className="h-4 w-4 text-orange-500" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </Button>
                        )}
                        {canDelete && !presentation.is_default && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(presentation)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPresentation ? 'Editar Presentación' : 'Nueva Presentación'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Ampolla, Comprimido"
                  required
                  data-testid="input-nombre"
                />
              </div>
              <div>
                <Label>Unidad Asociada</Label>
                <Input
                  value={formData.unidad_asociada}
                  onChange={(e) => setFormData(prev => ({ ...prev, unidad_asociada: e.target.value }))}
                  placeholder="Ej: mg, ml, UI"
                  data-testid="input-unidad"
                />
              </div>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción de la presentación"
                rows={2}
                data-testid="input-descripcion"
              />
            </div>
            <div className="w-24">
              <Label>Orden</Label>
              <Input
                type="number"
                value={formData.orden}
                onChange={(e) => setFormData(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                min={0}
                data-testid="input-orden"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-presentation">
                {editingPresentation ? 'Guardar Cambios' : 'Crear Presentación'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
